import { captureException } from '@sentry/react';
import isObject from 'lodash/isObject';
import { Channel, Socket } from 'phoenix';
import { queryClient } from '~/client/queryClient';
import i18n from '~/locales/i18n';
import { isDefined } from '~/types/typeGuards';
import { User, UserQueryKeys } from '~/types/user';

export interface SibillSocketChannel {
  registerEvent: <T>(eventName: string, handler: (payload: T) => void | Promise<void>) => () => void;
  leave: () => void;
}

export interface SibillSocket {
  isConnected: () => boolean;
  connect: () => Promise<boolean>;
  disconnect: () => Promise<boolean>;
  joinChannel: (channelName: string, params?: object) => SibillSocketChannel;
}

const disconnectSocket = (socket: Socket): Promise<boolean> => {
  return new Promise<boolean>((res) =>
    socket.disconnect(() => {
      // eslint-disable-next-line no-console
      console.debug('[SibillSocket::disconnect] socket disconnected.');

      return res(true);
    }),
  );
};

const connectSocket = (socket: Socket): Promise<boolean> => {
  if (!socket.isConnected()) {
    socket.connect();
    // eslint-disable-next-line no-console
    console.debug(`[SibillSocket::connect] socket is ${socket.connectionState()}.`);
  } else {
    // eslint-disable-next-line no-console
    console.debug(`[SibillSocket::connect] socket already ${socket.connectionState()}.`);
  }
  return Promise.resolve(true);
};

const createSibillChannel = (channel: Channel): SibillSocketChannel => ({
  registerEvent: (eventName, handler) => {
    const ref = channel.on(eventName, handler);
    return () => channel.off(eventName, ref);
  },
  leave: () => {
    if (channel.state === 'leaving' || channel.state === 'closed') return;

    channel
      .leave()
      .receive('ok', () => {
        // eslint-disable-next-line no-console
        console.debug(`[SibillSocket::leaveChannel] Channel ${channel.topic} left`);
      })
      .receive('error', (resp) => {
        const user: User | undefined = queryClient.getQueryData(UserQueryKeys.currentUser);
        captureException(resp, { extra: { topic: channel.topic, userId: user?.id } });

        // eslint-disable-next-line no-console
        console.debug(`[SibillSocket::leaveChannel] Unable to leave channel ${channel.topic}`);
      });
  },
});

const joinChannel = (socket: Socket, channelName: string, params?: object): SibillSocketChannel => {
  const newChannel = socket.channel(channelName, params);

  newChannel
    .join()
    .receive('ok', () => {
      // eslint-disable-next-line no-console
      console.debug(`[SibillSocket::joinChannel] Joined channel ${channelName}`);
    })
    .receive('error', (resp: { reason: string }) => {
      if (resp.reason !== 'unauthorized') {
        const user: User | undefined = queryClient.getQueryData(UserQueryKeys.currentUser);
        captureException(resp, { extra: { channelName, userId: user?.id } });
        // eslint-disable-next-line no-console
        console.debug(`[SibillSocket::joinChannel] Unable to join ${channelName}. Retry logic is in progress...`, resp);
      } else {
        // eslint-disable-next-line no-console
        console.debug(`[SibillSocket::joinChannel] Unable to join ${channelName}. Unauthorized`);
        newChannel.leave();
      }
    });

  return createSibillChannel(newChannel);
};

export const createSocket = (token: string): SibillSocket => {
  // eslint-disable-next-line no-console
  console.debug(`[SibillSocket::createSocket] Create socket ${token}`);

  const newSocket = new Socket(import.meta.env.VITE_WS_BACKEND_URL, {
    params: () => ({
      token,
      locale: i18n.language,
    }),
    reconnectAfterMs: (tries) => {
      const delay = [1000, 2000, 5000, 10000][tries - 1] || 30000;
      // eslint-disable-next-line no-console
      console.debug(`[SibillSocket::reconnect] Attempt #${tries}, reconnecting in ${delay}ms`);
      return delay;
    },
    timeout: 10000,
  });

  newSocket.onOpen(() => {
    // eslint-disable-next-line no-console
    console.debug('[SibillSocket::onOpen] socket connection opened');
  });

  newSocket.onError((error) => {
    const user: User | undefined = queryClient.getQueryData(UserQueryKeys.currentUser);
    const extra: Record<string, unknown> = {
      userId: user?.id,
    };

    if (isDefined(error) && isObject(error)) {
      extra.errorIsTrusted = error.isTrusted;
      extra.errorType = error.type;
      extra.currentTarget = error.currentTarget;
      extra.target = error.target;
    } else {
      extra.error = error;
    }
    captureException(new Error('Socket error'), { extra });
  });

  newSocket.onClose((event) => {
    // eslint-disable-next-line no-console
    console.debug(`[SibillSocket::onClose] socket close ${token}`, event);

    if (event.code !== 1000 && event.code !== 1006) {
      captureException(new Error('Socket closed with unhandled message.'), { extra: { event } });
    }
  });

  return {
    isConnected: newSocket.isConnected,
    disconnect: () => disconnectSocket(newSocket),
    connect: () => connectSocket(newSocket),
    joinChannel: (channelName: string, params?: object) => joinChannel(newSocket, channelName, params),
  };
};
